import React, { useEffect, useState } from "react";
import { decodeHtml } from "../../../../../assests/extention/commonfunction";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import CloudStorage from "../../../../../CloudStorage/CloudStorage";
import LoginLoaderButton from "../../../../Common/Loader/LoginLoaderButton";
import { currentYear } from "../../../../Common/FewCommonMethods";
import AsteriskLabel from "../../../../Common/AsteriskLabel";
import YearRange from "../../../../Common/YearRange";

const validationSchema = Yup.object().shape({
  designation: Yup.string().required("Designation is required"),
  company: Yup.string().required("Company is required"),
  from_year: Yup.date()
    .required("From date is required")
    .typeError("Please enter a valid date")
    .max(new Date(), "From date cannot be in the future")
    .min(new Date(1900, 0, 1), "From date cannot be before 1900"),
  to_year: Yup.date()
    .nullable() // Allow null for currently working
    .when("currently_working", {
      is: 0, // When NOT currently working
      then: (schema) => schema
        .required("To date is required when not currently working")
        .typeError("Please enter a valid date")
        .max(new Date(), "To date cannot be in the future")
        .min(new Date(1900, 0, 1), "To date cannot be before 1900")
        .test(
          "date-comparison",
          "To date must be after From date",
          function (value) {
            const { from_year, currently_working } = this.parent;
            if (currently_working === 1 || !value || !from_year) return true;
            
            const fromDate = new Date(from_year);
            const toDate = new Date(value);
            
            // Ensure at least one day difference
            fromDate.setHours(0, 0, 0, 0);
            toDate.setHours(0, 0, 0, 0);
            
            const timeDiff = toDate.getTime() - fromDate.getTime();
            const dayDiff = timeDiff / (1000 * 3600 * 24);
            
            return dayDiff >= 1; // At least one day gap
          }
        )
        .test(
          "max-duration",
          "Duration cannot exceed 60 years",
          function (value) {
            const { from_year, currently_working } = this.parent;
            if (currently_working === 1 || !value || !from_year) return true;
            
            const fromDate = new Date(from_year);
            const toDate = new Date(value);
            
            // Calculate year gap
            let yearGap = toDate.getFullYear() - fromDate.getFullYear();
            const monthDiff = toDate.getMonth() - fromDate.getMonth();
            const dayDiff = toDate.getDate() - fromDate.getDate();
            
            // Adjust year gap if the end date hasn't reached the anniversary
            if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
              yearGap--;
            }
            
            return yearGap <= 60; // Maximum 60 years
          }
        ),
      otherwise: (schema) => schema.nullable() // Allow null for currently working
    }),
  currently_working: Yup.number().oneOf([0, 1], "Invalid value"),
});

const IAddExperience = ({ formData, setFormData, nextPage, previousPage }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [updateIndex, setUpdateIndex] = useState(null);
  const [validation, setValidation] = useState(false);
  const [loading, setLoading] = useState(false);

  const openModal = (index) => {
    setModalOpen(true);
    setUpdateIndex(index);
  };

  const closeModal = () => {
    setModalOpen(false);
    setUpdateIndex(null);
  };

  const modalStyle = {
    right: modalOpen ? "0" : "-100%",
  };

  const handleSubmit = (values, { resetForm }) => {
    const formattedValues = {
      ...values,
      // Format dates for storage
      from_year: formatDateForStorage(values.from_year),
      to_year: values.currently_working === 1 ? "" : formatDateForStorage(values.to_year),
      // Convert currently_working to number if needed
      currently_working: values.currently_working === 1 ? 1 : 0,
    };

    if (updateIndex !== null) {
      setFormData((prevData) => ({
        ...prevData,
        experience: prevData.experience.map((exp, index) =>
          index === updateIndex ? formattedValues : exp,
        ),
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        experience: [...prevData.experience, formattedValues],
      }));
    }
    setValidation(false);
    closeModal();
    resetForm();
  };

  const SubmitandNext = async () => {
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    if (formData.experience?.length > 0) {
      nextPage();
    } else {
      setValidation(true);
    }
    setLoading(false);
  };

  const deleteExperience = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      experience: prevData.experience.filter((_, i) => i !== index),
    }));
  };

  const updateExperience = (index) => {
    openModal(index);
  };

  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [modalOpen]);

  // Helper function to format date for storage
  const formatDateForStorage = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    
    // If it's already in YYYY-MM-DD format, return as is
    if (dateString.includes("-") && dateString.split("-").length === 3) {
      return dateString;
    }
    
    // If it's just a year, add January 1st
    if (/^\d{4}$/.test(dateString)) {
      return `${dateString}-01-01`;
    }
    
    // Try to parse as date
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get year from date string
  const getYearFromDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date;
  };

  // Helper function to calculate day difference
  const calculateDayDifference = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 0;
    
    const from = new Date(fromDate);
    const to = new Date(toDate);
    
    from.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);
    
    const timeDiff = to.getTime() - from.getTime();
    return Math.floor(timeDiff / (1000 * 3600 * 24));
  };

  // Helper to format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      {modalOpen && (
        <div className="add-education-modal add-overlay-modal-width active" style={modalStyle}>
          <div className={`modal-overlay-sec   ${modalOpen ? 'active' : ''}`}></div>
          <div className="modal-width">
            <div className="modal-head">
              <h3>{updateIndex !== null ? "Edit" : "Add"} Experience</h3>
              <button onClick={closeModal} className="btn close-modal-btn">
                <img
                  src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/close-icon.svg`}
                  alt=""
                />
              </button>
            </div>
            <Formik
              initialValues={
                updateIndex !== null
                  ? {
                    ...formData.experience[updateIndex],
                    from_year: formatDateForInput(formData.experience[updateIndex].from_year),
                    to_year: formData.experience[updateIndex].currently_working === 1 ? 
                      "" : 
                      formatDateForInput(formData.experience[updateIndex].to_year),
                    currently_working: formData.experience[updateIndex].currently_working || 0,
                  }
                  : {
                    designation: "",
                    company: "",
                    from_year: "",
                    to_year: "",
                    currently_working: 0,
                  }
              }
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ setFieldValue, values, errors, touched }) => {
                const dayDifference = values.currently_working === 1 ? 0 : 
                  calculateDayDifference(values.from_year, values.to_year);
                const today = new Date().toISOString().split('T')[0];
                
                return (
                  <Form>
                    <div className="form-group">
                      <label className="label">Designation <AsteriskLabel /></label>
                      <Field
                        className="form-control"
                        type="text"
                        name="designation"
                        placeholder="Enter Designation"
                      />
                      <ErrorMessage
                        name="designation"
                        component="div"
                        className="error"
                      />
                    </div>
                    <div className="form-group">
                      <label className="label">Company <AsteriskLabel /></label>
                      <Field
                        className="form-control"
                        type="text"
                        name="company"
                        placeholder="Enter Company name"
                      />
                      <ErrorMessage
                        name="company"
                        component="div"
                        className="error"
                      />
                    </div>

                    {/* <div className="form-group mb-3">
                      <label className="d-flex align-items-center">
                        <Field
                          type="checkbox"
                          name="currently_working"
                          className="me-2"
                          checked={values.currently_working === 1}
                          onChange={(e) => {
                            setFieldValue("currently_working", e.target.checked ? 1 : 0);
                            if (e.target.checked) {
                              setFieldValue("to_year", "");
                            }
                          }}
                        />
                        I am currently working in this role
                      </label>
                    </div> */}

                    <h4 className="work-title">Employment Dates</h4>
                    <div className="row">
                      <div className="form-group col-md-6">
                        <label className="label">From Date <AsteriskLabel /></label>
                        <input
                          type="date"
                          className={`form-control ${touched.from_year && errors.from_year ? 'is-invalid' : ''}`}
                          value={values.from_year}
                          onChange={(e) => {
                            setFieldValue("from_year", e.target.value);
                          }}
                          min="1900-01-01"
                          max={today}
                          disabled={values.currently_working === 1}
                        />
                        <ErrorMessage
                          name="from_year"
                          component="div"
                          className="error"
                        />
                        {/* <small className="text-muted">
                          Selected: {values.from_year ? formatDateForDisplay(values.from_year) : "Not selected"}
                        </small> */}
                      </div>
                      <div className="form-group col-md-6">
                        <label className="label">
                          To Date {values.currently_working === 0 && <AsteriskLabel />}
                        </label>
                        <input
                          type="date"
                          className={`form-control ${touched.to_year && errors.to_year ? 'is-invalid' : ''}`}
                          value={values.to_year}
                          onChange={(e) => {
                            setFieldValue("to_year", e.target.value);
                          }}
                          min={values.from_year ? 
                            new Date(new Date(values.from_year).getTime() + 86400000).toISOString().split('T')[0] : 
                            "1900-01-01"
                          }
                          max={today}
                          disabled={values.currently_working === 1}
                          readOnly={values.currently_working === 1}
                        />
                        <ErrorMessage
                          name="to_year"
                          component="div"
                          className="error"
                        />
                        {/* <small className="text-muted">
                          {values.currently_working == 1 ? (
                            "Currently working here"
                          ) : (
                            <>
                              Selected: {values.to_year ? formatDateForDisplay(values.to_year) : "Not selected"}
                              {values.from_year && values.to_year && dayDifference > 0 && (
                                <span style={{ color: dayDifference < 365 ? 'orange' : 'green', marginLeft: '10px' }}>
                                  ({dayDifference} days / {(dayDifference/365).toFixed(1)} years)
                                </span>
                              )}
                            </>
                          )}
                        </small> */}
                      </div>
                    </div>

                    {/* Duration summary */}
                    {/* {values.currently_working === 0 && values.from_year && values.to_year && dayDifference > 0 && (
                      <div className="alert alert-info mt-3">
                        <small>
                          <strong>Duration:</strong> {dayDifference} days ({getYearFromDate(values.from_year)} to {getYearFromDate(values.to_year)})<br />
                          <strong>Validation:</strong> {
                            dayDifference < 1 ? "❌ Must be at least 1 day" :
                            dayDifference > 21915 ? "❌ Cannot exceed 60 years" :
                            "✓ Valid duration"
                          }
                        </small>
                      </div>
                    )} */}

                    {/* {values.currently_working === 1 && values.from_year && (
                      <div className="alert alert-success mt-3">
                        <small>
                          <strong>Current Employment:</strong> Started on {formatDateForDisplay(values.from_year)}<br />
                          <strong>Duration to date:</strong> {calculateDayDifference(values.from_year, today)} days ({(calculateDayDifference(values.from_year, today)/365).toFixed(1)} years)
                        </small>
                      </div>
                    )} */}

                    <div className="add-btn">
                      <button type="submit" className="btn btn-primary">
                        {updateIndex !== null ? "Update" : "Add"} Experience
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      )}
      <div className="row about-row">
        <div className="col-lg-4 about-left">
          <h6>
            <span className="about-step">05/9</span> <span>Previous Experience</span>
          </h6>
          <h2>What is your working experience?</h2>
          <p>Submit Your Professional Experience and Specify Your Ideal Work Environment and Preferences</p>
        </div>
        <div className="col-lg-6 about-right">
          <h6>Work Experience <AsteriskLabel /></h6>
          {formData?.experience?.map((item, index) => {
            const fromYear = getYearFromDate(item.from_year);
            const toYear = item.currently_working === 1 ? 
              "Present" : 
              getYearFromDate(item.to_year);
            const dayDiff = item.currently_working === 1 ? 
              calculateDayDifference(item.from_year, new Date()) : 
              calculateDayDifference(item.from_year, item.to_year);
            
            return (
              <div key={index} className="add-education-wrapper">
                <div className="add-first">
                  <div>
                    <img
                      src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/bag.svg`}
                      alt=""
                    />
                  </div>
                  <div>
                    <h2>{decodeHtml(item.designation)}</h2>
                    <h5>{decodeHtml(item.company)}</h5>
                    <YearRange 
                      fromYear={fromYear} 
                      toYear={toYear} 
                      isPresent={item.currently_working === 1}
                    />
                    {/* {dayDiff > 0 && (
                      <small className="text-muted">
                        ({dayDiff} days / {(dayDiff/365).toFixed(1)} years)
                        {item.currently_working === 1 && " • Currently working"}
                      </small>
                    )} */}
                  </div>
                </div>
                <div className="add-button-education">
                  <button onClick={() => deleteExperience(index)}>
                    <img
                      src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/delete-icon.svg`}
                      alt=""
                    />
                  </button>
                  <button onClick={() => updateExperience(index)}>
                    <img
                      src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/edit.svg`}
                      alt=""
                    />
                  </button>
                </div>
              </div>
            );
          })}
          <button className="add-education-btn" onClick={() => openModal(null)}>
            Add Experience
          </button>
          {validation && (
            <p style={{ color: "red" }}>At Least Add one Experience</p>
          )}
        </div>
      </div>
      <div className="button-container">
        <button
          disabled={loading}
          type="button"
          onClick={() => previousPage()}
          className="prev-button"
        >
          <img
            src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/back-arrow.svg`}
            alt=""
          />
          Back
        </button>
        <button
          type="button"
          onClick={() => SubmitandNext()}
          className="next-button"
        >
          {loading ? (
            <LoginLoaderButton />
          ) : (
            <>
              Next
              <img
                src={`${CloudStorage.cloudurl}/Assets/Images/common/right-arrow.svg`}
                alt=""
              />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default IAddExperience;